import {
  APP_INITIALIZER,
  inject,
  makeEnvironmentProviders,
  type EnvironmentProviders,
} from '@angular/core';
import { provideHttpClient } from '@angular/common/http';

import {
  provideAuth as provideOidcAuth,
  LogLevel,
  StsConfigLoader,
  StsConfigStaticLoader,
  type OpenIdConfiguration,
} from 'angular-auth-oidc-client';
import { AbstractSecurityStorage } from 'angular-auth-oidc-client';

import { APP_CONFIG, type AppConfig, type IssuerAuthConfig } from '../config/app-config.model';
import { REAL_ACCESS_TOKEN_PROVIDER } from '../realtime/access-token.provider';
import { provideAuthTelemetry } from './application-insights-auth-telemetry';
import { AuthFlowService } from './auth-flow.service';
import {
  CLIENT_CONFIG_ID,
  STAFF_CONFIG_ID,
  type IssuerCategory,
} from './claims-mapping';
import { InMemorySecurityStorage } from './in-memory-security-storage';
import { OidcAccessTokenProvider } from './oidc-access-token.provider';

export { CLIENT_CONFIG_ID, STAFF_CONFIG_ID };

/** Seconds before expiry at which silent renewal kicks in (FR-013, R-105). */
export const RENEW_TIME_BEFORE_TOKEN_EXPIRES_SECONDS = 300;

/**
 * Builds the named OIDC configurations from the validated runtime config
 * (data-model.md §3): auth code + PKCE, refresh-token silent renewal at
 * T−300 s, redirect and post-logout URI `<origin>/sign-in` (the route is
 * already public — no new route, R-110), shared in-memory storage. The
 * `client` configuration exists only when `auth.client !== null` (FR-020).
 *
 * Pure for testability; returns an empty list when identity is not
 * configured (mockAuth dev mode) so the library stays inert.
 */
export function buildOpenIdConfigurations(
  appConfig: AppConfig,
  origin: string,
): OpenIdConfiguration[] {
  if (appConfig.mockAuth || appConfig.auth === null) return [];

  const configurations = [
    toOpenIdConfiguration(STAFF_CONFIG_ID, appConfig.auth.staff, origin),
  ];
  if (appConfig.auth.client !== null) {
    configurations.push(toOpenIdConfiguration(CLIENT_CONFIG_ID, appConfig.auth.client, origin));
  }
  return configurations;
}

function toOpenIdConfiguration(
  configId: IssuerCategory,
  issuer: IssuerAuthConfig,
  origin: string,
): OpenIdConfiguration {
  return {
    configId,
    authority: issuer.authority,
    clientId: issuer.clientId,
    scope: [...issuer.scopes, issuer.apiScope].join(' '),
    responseType: 'code',
    redirectUrl: `${origin}/sign-in`,
    postLogoutRedirectUri: `${origin}/sign-in`,
    silentRenew: true,
    useRefreshToken: true,
    renewTimeBeforeTokenExpiresInSeconds: RENEW_TIME_BEFORE_TOKEN_EXPIRES_SECONDS,
    // Claims are read from the validated ID token only (R-104); the userinfo
    // endpoint is never called.
    autoUserInfo: false,
    // AuthFlowService owns all post-callback routing (R-110) — the library
    // emits an authorization-result event instead of navigating itself.
    triggerAuthorizationResultEvent: true,
    // Microsoft Entra ID back-dates `iat` and `nbf` by a fixed five minutes so
    // that resource servers with slow clocks never reject a freshly minted
    // token. The library's `iat` check is symmetric — it rejects when
    // |now - iat| exceeds the allowance — and its default allowance is 120 s,
    // so every real Entra sign-in failed that gate with
    // `MaxOffsetExpired` while the clock was correct (measured: iat and nbf
    // both exactly 301 s in the past, `exp` a clean hour ahead, `auth_time`
    // absent). The substitute IdP mints `iat` at redemption time, which is why
    // no e2e run could reproduce it.
    //
    // 600 s = Entra's 300 s back-date plus 300 s of genuine clock-skew
    // tolerance, the conventional allowance. The offset check is a
    // non-normative hardening against replay of a long-hoarded token; `exp`,
    // `nonce`, the signature, `aud` and `iss` are the load-bearing gates and
    // are all unchanged.
    maxIdTokenIatOffsetAllowedInSeconds: 600,
    // Zero-console-errors posture (FR-042): only genuine library errors log.
    logLevel: LogLevel.Error,
  };
}

/**
 * The single identity entry point (FR-017 portability, R-112): registers the
 * OIDC library with the configurations derived from APP_CONFIG.auth, binds
 * the in-memory credential storage (FR-015, R-103), and provides the closed
 * auth telemetry seam (R-111).
 *
 * Eager by design — session derivation and guards run at startup (plan
 * Technical Context). Wired into app.config.ts by T017.
 */
export function provideAuth(): EnvironmentProviders {
  return makeEnvironmentProviders([
    InMemorySecurityStorage,
    // The OIDC library's discovery/token transport is HttpClient. App code
    // remains banned from @angular/common/http everywhere else — this is the
    // library's transport seam only (no interceptors are registered; the
    // AccessTokenProvider port stays the single token egress, R-106).
    provideHttpClient(),
    provideOidcAuth({
      loader: {
        provide: StsConfigLoader,
        useFactory: () =>
          new StsConfigStaticLoader(
            buildOpenIdConfigurations(inject(APP_CONFIG), window.location.origin),
          ),
      },
    }),
    // After the library's own providers: provideAuth registers its default
    // session-storage service for AbstractSecurityStorage, and the later
    // registration wins — the in-memory storage must come last (FR-015).
    { provide: AbstractSecurityStorage, useExisting: InMemorySecurityStorage },
    // The identity module's binding of the AITH-31 token port (R-106).
    { provide: REAL_ACCESS_TOKEN_PROVIDER, useExisting: OidcAccessTokenProvider },
    provideAuthTelemetry(),
    // FR-010 no-flash: the OIDC callback is processed during bootstrap, before
    // the router renders any destination. Inert in mockAuth dev mode.
    {
      provide: APP_INITIALIZER,
      multi: true,
      useFactory: () => {
        const flow = inject(AuthFlowService);
        return () => flow.completeBootstrapCallback();
      },
    },
  ]);
}
